import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DocumentService, TenantVM } from "@propertelligent/client-api";
import { GetImages } from "../utils/GetImages";
import apiHelper from "../utils/apiHelper";
import { convertStreamAndDownload } from "../utils/helper";
import LargeTooltip from "../newComponents/tooltips/large-tooltip";
import SimpleSpinner from "../components/loader";
import CurrencyTooltip from "../components/currencytooltip/CurrencyTooltip";
import BluePayableIcon from "../assets/Payment-Icon.svg";

interface TooltipPosition {
  top: number;
  left: number;
  placement?: 'top' | 'bottom';
}

interface LeaseTooltipContentProps {
  listingData: TenantVM;
  navigate: ReturnType<typeof useNavigate>;
  onViewAgreement?: () => void;
  onViewApplication?: () => void;
}

const LeaseTooltipContent: React.FC<LeaseTooltipContentProps> = ({
  listingData,
  navigate,
  onViewAgreement,
  onViewApplication
}) => {
  const [loadingType, setLoadingType] = useState<
    "agreement" | "application" | null
  >(null);

  const handleDownload = async (
    docId: number,
    fileName: string,
    type: "agreement" | "application",
  ) => {
    try {
      setLoadingType(type);
      const response = await apiHelper(
        DocumentService.getApiDocumentBase641,
        { showNotification: false },
        docId,
      );

      if (response?.data?.success) {
        convertStreamAndDownload(response.data.success, fileName);
      } else {
        console.error("No PDF found");
      }
    } catch (error) {
      console.error("Download failed", error);
    } finally {
      setLoadingType(null);
    }
  };

  const handleViewAgreement = () => {
    if (listingData?.currentLeaseAgreementDocumentId) {
      handleDownload(
        listingData.currentLeaseAgreementDocumentId,
        `LeaseAgreement-${listingData.currentLeaseAgreementDocumentId}.pdf`,
        "agreement",
      );
    } else if ((listingData as any)?.usedLeaseAgreementTemplate) {
      if (onViewAgreement) {
        onViewAgreement();
      } else {
        navigate(`/tenants/review-agreement/${listingData.id}`);
      }
    }
  };

  const handleViewApplication = () => {
    if (listingData?.currentLeaseApplicationDocumentId) {
      handleDownload(
        listingData.currentLeaseApplicationDocumentId,
        `LeaseApplication-${listingData.currentLeaseApplicationDocumentId}.pdf`,
        "application",
      );
    } else if ((listingData as any)?.usedLeaseApplicationTemplate) {
      if (onViewApplication) {
        onViewApplication();
      } else {
        navigate(`/tenants/review-application/${listingData.id}`);
      }
    }
  };

  const isAgreementDisabled =
    !(listingData as any)?.usedLeaseAgreementTemplate &&
    !listingData?.currentLeaseAgreementDocumentId;
  const isApplicationDisabled =
    !(listingData as any)?.usedLeaseApplicationTemplate &&
    !listingData?.currentLeaseApplicationDocumentId;

  return (
    <div className="lease-summary-card">
      {/* Header */}
      <div className="lease-summary-card__header">
        <div className="lease-summary-card__lease-type">
          <div className="lease-summary-card__label">
            <img
              src={GetImages.documentIcon}
              alt="lease-icon"
              width="20"
              height="20"
              className="stat-icon"
            />
            Lease Type:
          </div>
          <span className="lease-summary-card__badge">
            {listingData?.formatted?.LeaseTypeId === 'Month-to-Month'
              ? 'Month To Month'
              : 'Fixed Term'}
          </span>
        </div>
      </div>

      {/* Dates */}
      <div className="lease-summary-card__dates">
        <div className="lease-summary-card__date-item">
          <div className="lease-summary-card__date-label">
            <img
              src={GetImages.calendarIcon}
              alt="lease-icon"
              width="20"
              height="20"
              className="stat-icon"
            />
            Start Date
          </div>
          <div className="lease-summary-card__date-value">
            {listingData?.formatted?.StartDateDateOnly || "N/A"}
          </div>
        </div>
        <div className="lease-summary-card__date-item">
          <div className="lease-summary-card__date-label">
            <img
              src={GetImages.calendarIcon}
              alt="lease-icon"
              width="20"
              height="20"
              className="stat-icon"
            />
            End Date
          </div>
          <div className="lease-summary-card__date-value">
            {listingData?.formatted?.EndDateDateOnly || "N/A"}
          </div>
        </div>
      </div>

      {/* Amounts */}
      <div className="lease-summary-card__amounts">
        <div className="lease-summary-card__amount-item">
          <div className="lease-summary-card__amount-label">
            <img
              src={GetImages.secureIcon}
              alt="lease-icon"
              width="20"
              height="20"
              className="stat-icon"
            />
            Security Deposit
          </div>
          <div className="lease-summary-card__amount-value">
            <CurrencyTooltip
              content={listingData?.formatted?.SecurityDepositRaw}
              formatted={listingData?.formatted?.SecurityDeposit}
            >
              <span>{listingData?.formatted?.SecurityDeposit || "0.00"}</span>
            </CurrencyTooltip>
          </div>
        </div>
        <div className="lease-summary-card__amount-item">
          <div className="lease-summary-card__amount-label">
            <img
              src={BluePayableIcon}
              alt="lease-icon"
              width="20"
              height="20"
              className="stat-icon rent-icon iconStyleWithWhiteFilter"
            />
            Rent
          </div>
          <div className="lease-summary-card__amount-value">
            <CurrencyTooltip
              content={listingData?.formatted?.RentRaw}
              formatted={listingData?.formatted?.Rent}
            >
              <span>{listingData?.formatted?.Rent || "0.00"}</span>
            </CurrencyTooltip>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="lease-summary-card__actions">
        <button
          className="lease-summary-card__btn lease-summary-card__btn--secondary"
          onClick={handleViewAgreement}
          disabled={isAgreementDisabled || loadingType === "agreement"}
          style={{
            opacity: isAgreementDisabled ? 0.5 : 1,
            cursor: isAgreementDisabled ? "not-allowed" : "pointer",
          }}
        >
          {loadingType === "agreement" ? (
            <SimpleSpinner myClassName="Icon20" />
          ) : isAgreementDisabled ? (
            "Agreement Not Available"
          ) : listingData?.currentLeaseAgreementDocumentId ? (
            "Download Agreement"
          ) : (
            "View Agreement"
          )}
        </button>

        <button
          className="lease-summary-card__btn lease-summary-card__btn--secondary"
          onClick={handleViewApplication}
          disabled={isApplicationDisabled || loadingType === "application"}
          style={{
            opacity: isApplicationDisabled ? 0.5 : 1,
            cursor: isApplicationDisabled ? "not-allowed" : "pointer",
          }}
        >
          {loadingType === "application" ? (
            <SimpleSpinner myClassName="Icon20" />
          ) : isApplicationDisabled ? (
            "Application Not Available"
          ) : listingData?.currentLeaseApplicationDocumentId ? (
            "Download Application"
          ) : (
            "View Application"
          )}
        </button>
      </div>
    </div>
  );
};

export const useLeaseInfoTooltip = () => {
  const [tooltipState, setTooltipState] = useState<{
    isOpen: boolean;
    description: React.ReactNode;
    position?: TooltipPosition;
    title?: string;
  }>({
    isOpen: false,
    description: "",
    position: { top: 0, left: 0, placement: 'bottom' },
    title: "Details",
  });

  useEffect(() => {
    const handleCloseAll = () => {
      setTooltipState((prev) => prev.isOpen ? { ...prev, isOpen: false } : prev);
    };

    window.addEventListener("closeAllLeaseTooltips", handleCloseAll);
    return () => {
      window.removeEventListener("closeAllLeaseTooltips", handleCloseAll);
    };
  }, []);

  const navigate = useNavigate();

  const getPosition = (event: React.MouseEvent<HTMLAnchorElement> | null, forcedPosition?: TooltipPosition): TooltipPosition => {
    if (forcedPosition) return forcedPosition;
    if (event && event.currentTarget) {
      const rect = event.currentTarget.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const spaceBelow = windowHeight - rect.bottom;
      const tooltipHeight = 400; // Estimated height

      let placement: 'top' | 'bottom' = 'bottom';
      let top = rect.bottom + window.scrollY + 8;

      if (spaceBelow < tooltipHeight) {
        placement = 'top';
        top = rect.top + window.scrollY - 10;
      }

      return {
        top,
        left: rect.left + window.scrollX,
        placement
      };
    }
    return { top: 0, left: 0, placement: 'bottom' };
  };

  const handleLeaseInfoClick = (
    event: React.MouseEvent<HTMLAnchorElement> | null,
    listingData: TenantVM,
    forcedPosition?: TooltipPosition,
    onViewAgreement?: () => void,
    onViewApplication?: () => void
  ) => {
    if (event) event.preventDefault();
    window.dispatchEvent(new Event("closeAllLeaseTooltips"));

    const position = getPosition(event, forcedPosition);

    const description = (
      <LeaseTooltipContent
        listingData={listingData}
        navigate={navigate}
        onViewAgreement={onViewAgreement}
        onViewApplication={onViewApplication}
      />
    );

    setTooltipState({
      isOpen: true,
      description: description,
      position: position,
      title: "Lease Details",
    });
  };

  const handleTenantInfoClick = (
    event: React.MouseEvent<HTMLAnchorElement> | null,
    tenant: TenantVM,
    forcedPosition?: TooltipPosition
  ) => {
    if (event) event.preventDefault();
    window.dispatchEvent(new Event("closeAllLeaseTooltips"));

    const position = getPosition(event, forcedPosition);

    const description = (
      <div className="contact-summary-card">
        <div className="contact-summary-card__item py-2">
          <div className="lease-summary-card__label">
            <img src={GetImages.emailIcon} alt="email" width="20" height="20" className="stat-icon" />
            Email:
          </div>
          <div className="lease-summary-card__date-value d-flex align-items-center">
            {tenant?.email || "---"}
            <CopyButton text={tenant?.email || ""} />
          </div>
        </div>

        <div className="contact-summary-card__item py-2">
          <div className="lease-summary-card__label">
            <img src={GetImages.contactIcon} alt="phone" width="20" height="20" className="stat-icon" />
            Phone:
          </div>
          <div className="lease-summary-card__date-value d-flex align-items-center">
            {tenant?.formatted?.PhoneNumber || "---"}
            <CopyButton text={"+"+tenant?.phoneNumber || ""} />
          </div>
        </div>
      </div>
    );

    setTooltipState({
      isOpen: true,
      description: description,
      position: position,
      title: "Tenant Details",
    });
  };

  const handleCloseTooltip = () => {
    setTooltipState((prev) => ({ ...prev, isOpen: false }));
  };

  const LeaseInfoTooltip = () => (
    tooltipState.isOpen ? (
      <div className="info-tooltip-wrapper">
      <LargeTooltip
        isOpen={tooltipState.isOpen}
        onClose={handleCloseTooltip}
        description={tooltipState.description}
        position={tooltipState.position}
        title={tooltipState.title || "Details"}
        type={"tenant"}
        placement={tooltipState.position.placement}
      />
      </div>
    ) : null
  );

  return {
    handleLeaseInfoClick,
    handleTenantInfoClick,
    handleCloseTooltip,
    LeaseInfoTooltip,
    setTooltipState,
    tooltipState
  };
};

const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);

    } catch (err) {
      console.error("Copy failed", err);
    }
  };

  return (
    <div
      onClick={handleCopy}
      className="clickable"
      style={{ display: 'inline-flex', alignItems: 'center' }}
      title="Copy to clipboard"
    >
      {copied ? (
        <span style={{ marginLeft: "6px", fontSize: "12px", color: "green", fontWeight: "bold" }}>
          Copied!
        </span>
      ) : (
        <img
          src={GetImages.copyIcon}
          alt="copy"
          width="16"
          height="16"
          className="copy-icon"
          style={{ marginLeft: "6px" }}
        />
      )}
    </div>
  );
};

